import gql from 'graphql-tag';
import React, {useCallback} from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible, assertAllAccessible} from 'graphql-client/InaccessibleInfo';
import {DocumentBanner} from 'notebook/components/banners/DocumentBanner';
import {hasUnsavedChanges} from 'notebook/entries/BaseEntryUtil';
import SubtemplateActions from 'notebook/entry-templates/SubtemplateActions';
import SubtemplateDAO from 'notebook/entry-templates/SubtemplateDAO';
import SubtemplateStore from 'notebook/entry-templates/SubtemplateStore';
import {
  SubtemplatePublishingRecordsQuery,
  SubtemplatePublishingRecordsQueryVariables,
} from 'notebook/notes/tabs/__generated/SubtemplatePublishingRecordsQuery';
import SubtemplateNoteView, {SubtemplateNoteViewProps} from 'notebook/notes/tabs/SubtemplateNoteView';
import {PublishingRecordForButton} from 'template-collectibles/publishing-ui/graphql/types';
import TemplatePublishingButton from 'template-collectibles/publishing-ui/TemplatePublishingButton';
import {getPublishableInfoForPublishingButton} from 'template-collectibles/publishing-ui/TemplatePublishingUtil';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

interface PublishingSubtemplateNoteViewProps extends SubtemplateNoteViewProps {
  openVersionApiId: string;
  openVersionModifiedAt: string;
}

export const SUBTEMPLATE_PUBLISHING_RECORDS_QUERY = gql`
  query SubtemplatePublishingRecordsQuery($subtemplateId: StrictString!)
  @skipAudit
  @engComponent(name: NOTEBOOK) {
    subtemplate(id: $subtemplateId) {
      ... on SubtemplateNode {
        id
        ...PublishingRecordsForButtonGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('PublishingRecordsForButton').fragment}
`;

export default function PublishingSubtemplateNoteView(
  props: PublishingSubtemplateNoteViewProps
): JSX.Element {
  const {fileId, openVersionApiId, openVersionModifiedAt, banner, isLatest} = props;

  const getPublishableInfoCallback = useCallback(
    (publishingRecords: Array<PublishingRecordForButton>) =>
      getPublishableInfoForPublishingButton(
        publishingRecords,
        openVersionApiId,
        openVersionModifiedAt,
        isLatest
      ),
    [openVersionApiId, openVersionModifiedAt, isLatest]
  );

  const onPublishCallback = useCallback(
    (subtemplateId: string, refetch: () => void) =>
      async (versionName: string, versionDescription?: string) => {
        await SubtemplateDAO.publishVersion(subtemplateId, openVersionApiId, versionName, versionDescription);
        const {openVersionId} = SubtemplateStore.getEditorState(subtemplateId);
        SubtemplateActions.loadVersion(subtemplateId, openVersionId);
        refetch();
      },
    [openVersionApiId]
  );

  const onWithdrawCallback = useCallback(
    (subtemplateId: string, refetch: () => void) => async () => {
      await SubtemplateDAO.withdrawVersion(subtemplateId, openVersionApiId);
      refetch();
    },
    [openVersionApiId]
  );

  const onCreateDraftCallback = useCallback(
    (subtemplateId: string) => async () => {
      const result = await SubtemplateDAO.createDraftFromVersion(subtemplateId, openVersionApiId);
      if (result) {
        const templateVersionDbId = result.draftSubtemplateVersionDbId;
        SubtemplateActions.loadVersion(subtemplateId, templateVersionDbId);
        SubtemplateActions.fetchVersions(subtemplateId);
      }
    },
    [openVersionApiId]
  );

  return (
    <BenchlingQuery<SubtemplatePublishingRecordsQuery, SubtemplatePublishingRecordsQueryVariables>
      query={SUBTEMPLATE_PUBLISHING_RECORDS_QUERY}
      variables={{subtemplateId: fileId}}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {({subtemplate}, {refetch}) => {
        assert(!isInaccessible(subtemplate), 'Expected subtemplate to be accessible.');
        const publishingRecords: Array<PublishingRecordForButton> = assertAllAccessible(
          subtemplate.publishingRecords
        );

        let isViewingPublishedVersion = false;
        let bannerToSet = null;
        const publishingRecord = publishingRecords.find((pr) => pr.publishableVersionId === openVersionApiId);
        if (publishingRecord) {
          isViewingPublishedVersion = true;
          bannerToSet = (
            <DocumentBanner.Publishing
              onCreateDraft={onCreateDraftCallback(subtemplate.id)}
              publishingStatus={publishingRecord.publishingStatus}
            />
          );
        }

        return (
          <SubtemplateNoteView
            {...props}
            isViewingPublishedVersion={isViewingPublishedVersion}
            // Assumes that the banner set by the SubtemplateFile takes precedence over the banner set by this component.
            banner={banner || bannerToSet}
            titleBadge={
              <TemplatePublishingButton
                {...getPublishableInfoCallback(publishingRecords)}
                publishingRecords={publishingRecords}
                publishableModelTypeName={ModelTypeConstants.SUBTEMPLATE}
                onPublish={onPublishCallback(subtemplate.id, refetch)}
                onWithdraw={onWithdrawCallback(subtemplate.id, refetch)}
                onCreateDraft={onCreateDraftCallback(subtemplate.id)}
                isPublishOptionDisabled={hasUnsavedChanges(subtemplate.id)}
              />
            }
          />
        );
      }}
    </BenchlingQuery>
  );
}
